<script lang="ts">
export default {
  name: 'ExtractedFieldsList',
};
</script>

<script setup lang="ts">
import { ref, computed, toRef, watch } from 'vue';
import { DateTime } from 'luxon';
import type { ParsedField } from '@/types/field';
import type {
  ReviewPreviewResponse,
  PolicySourceRestriction,
  ReviewEditInput,
  ReviewLineOpInput,
  EntityMatch,
  ExtractionTarget,
} from '@/types/ai';
import type { CoverageType } from '@/types/PolicyType';
import type { PromptField } from '@/types/PromptField';
import Badge from '@/components/ui/Badge.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import EditableField from '@/components/document/EditableField.vue';
import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
import CoverageLinesEditModal from '@/components/document/CoverageLinesEditModal.vue';
import PolicyEditReviewPanel from '@/components/v2/policy/PolicyEditReviewPanel.vue';
import AppModal from '@/components/v2/AppModal.vue';
import { buildReviewRecords, hasReviewChanges } from '@/components/document/reviewRecords';
import { useFieldOverrides } from '@/composables/useFieldOverrides';
import { aiService } from '@/services/AIService';
import { formatCurrency } from '@/utilities/Money';
import { isCurrencyField } from '@/utilities/CurrencyFields';
import { List, Search, X, AlertTriangle, ChevronRight, History, Loader2, Pencil, ChevronDown, ChevronUp, CheckCircle, Eye, Plus, Trash2, Undo2 } from '@lucide/vue';
import { CollapsibleRoot, CollapsibleContent, CollapsibleTrigger } from 'reka-ui';

const props = defineProps<{
  fields: ParsedField[];
  selectedField: ParsedField | null;
  error?: string;
  rawOutput?: string;
  promptResultId?: number;
  overriddenFields?: string[]; // List of field paths that have overrides (from merged endpoint)
  malformed?: boolean;
  policySourceRestriction?: PolicySourceRestriction | null;
  coverageTypes?: CoverageType[]; // Reference list for add/recategorize coverage line controls
  entityMatches?: EntityMatch[]; // Reference-data provenance per field path
  // Values an entity field may be corrected to, by entity type. Supplied only by the
  // surface that knows which reference data applies -- this list is shared with the
  // internal review, which resolves nothing against external policy reference data.
  entityOptions?: Partial<Record<EntityMatch['entityType'], string[]>>;
  // The group each selectable value rolls up to, keyed by value. A policy line stores the
  // child entity, so the group is shown beside the correction rather than corrected.
  entityGroups?: Record<string, string>;
  // Whether the reference lists failed to load. Falling back to free text would let a
  // correction through that the create then rejects, with nothing explaining why.
  entityOptionsFailed?: boolean;
  // What committing this review does. An external extraction creates a policy through the
  // binder confirm, so its edits are saved changes rather than a validation of a record.
  targetKind?: ExtractionTarget['kind'];
}>();

const isExternalTarget = computed(() => props.targetKind === 'new_external_policy');
const reviewActionLabel = computed(() => (isExternalTarget.value ? 'Edit fields' : 'Review'));
const reviewFinishLabel = computed(() => (isExternalTarget.value ? 'Save changes' : 'Finish Review'));
// There is no override on the external path -- the confirm creates the policy -- so the
// checkbox says what excluding actually does there: the field is left off the new policy.
const exclusionLabel = computed(() => (isExternalTarget.value ? 'Leave blank' : 'Exclude from override'));

// Provenance by field path, for the badge beside a field the agentic lookup resolved.
const matchesByPath = computed<Record<string, EntityMatch>>(() => {
  const map: Record<string, EntityMatch> = {};
  for (const match of props.entityMatches ?? []) {
    if (match.fieldPath) {
      map[match.fieldPath] = match;
    }
  }
  return map;
});

// Provenance, now that the field itself carries the resolved value: what the document said
// before it was matched. Repeating the resolved value here would show it twice and leave
// the document text nowhere.
function matchBadge(path: string): { label: string; class: string; title: string } | null {
  const match = matchesByPath.value[path];
  if (!match) {
    return null;
  }
  const source = match.sourceText ? `Matched from: "${match.sourceText}"` : 'Matched from the document';
  switch (match.status) {
    case 'matched':
      return { label: source, class: 'border-green-300 bg-green-100 text-green-700', title: source };
    case 'llm_matched':
      return { label: source, class: 'border-blue-300 bg-blue-100 text-blue-700', title: source };
    default:
      return { label: 'Not matched — choose a value', class: 'border-amber-300 bg-amber-100 text-amber-700', title: '' };
  }
}

// What the picker shows: the reviewer's correction if they made one, else the value the
// match resolved to. Not the field's own text -- that is the document's wording, which is
// never one of the options, and showing it would leave the control looking empty.
function entityPickerValue(field: ParsedField): string {
  const local = getFieldLocalValue(field);
  if (local !== undefined) {
    return String(local);
  }
  if (field.path && props.overriddenFields?.includes(field.path)) {
    return String(getDisplayValue(field) ?? '');
  }
  const match = field.path ? matchesByPath.value[field.path] : undefined;
  if (!match) {
    return '';
  }
  return match.matchedName ?? '';
}

// The group the picked value rolls up to. Derived, never edited: a policy line stores the
// child entity, and this is what it will be reported under.
function entityGroupFor(field: ParsedField): string {
  const value = entityPickerValue(field);
  if (!value) {
    return '';
  }
  const fromOptions = props.entityGroups?.[value];
  if (fromOptions) {
    return fromOptions;
  }
  const match = field.path ? matchesByPath.value[field.path] : undefined;
  return match?.matchedName === value ? (match.groupName ?? '') : '';
}

// The label for the group beside a correction, by what the match resolves.
function entityGroupLabel(path: string | undefined): string {
  const match = path ? matchesByPath.value[path] : undefined;
  switch (match?.entityType) {
    case 'carrier':
      return 'Carrier group';
    case 'wholesaler':
      return 'Wholesaler group';
    default:
      return '';
  }
}

// A field an extraction resolved against reference data, on a surface that creates a
// record from it. Its value is the resolved one, not the document's wording.
function isEntityField(path: string | undefined): boolean {
  return !!path && props.targetKind === 'new_external_policy' && !!matchesByPath.value[path];
}

// Correcting a resolved entity is picking from reference data, not typing: a free-text
// value the create does not recognise is rejected, so only the external review offers it.
function entityPickerOptions(path: string | undefined): ComboboxOption[] {
  if (!path || props.targetKind !== 'new_external_policy') {
    return [];
  }
  const match = matchesByPath.value[path];
  const values = match ? (props.entityOptions?.[match.entityType] ?? []) : [];
  return values.map((value) => ({ label: value, value }));
}

const isMalformed = computed(() => props.malformed ?? false);

const emit = defineEmits<{
  'field-selected': [field: ParsedField];
  'overrides-changed': []; // Emitted when an override is saved or reverted
  'review-completed': []; // Emitted when user finishes a review
  // Unsaved edit count, so a host whose own action would discard them can say so.
  'pending-changes': [count: number];
}>();

// Review mode state using composable
const promptResultIdRef = toRef(props, 'promptResultId');
const {
  isReviewMode,
  isLoading: isLoadingOverrides,
  isSavingAll,
  overrides,
  overrideCount,
  hasPendingChanges,
  pendingChangeCount,
  enterReviewMode,
  exitReviewMode,
  getOverride,
  hasOverride,
  isPending,
  getError,
  clearError,
  setLocalOverride,
  revertLocalOverride,
  setLocalRevert,
  isReverted,
  hasLocalChange,
  getLocalChange,
  setLocalExclusion,
  hasLocalExclusionChange,
  isExcluded,
  buildEditPayload,
  pendingAddedLines,
  addPendingLine,
  setPendingLineField,
  getPendingLineField,
  ensureSupersedingAdd,
  markLineDeleted,
  unmarkLineDeleted,
  isLineDeleted,
  setLineRecategorize,
  clearLineRecategorize,
  getPendingLineOp,
  buildLineOpsPayload,
} = useFieldOverrides(promptResultIdRef as any);

watch(
  () => (isReviewMode.value ? pendingChangeCount.value : 0),
  (count) => emit('pending-changes', count),
  { immediate: true },
);

const searchQuery = ref('');
const isHistoryExpanded = ref(false);
const collapsedPaths = ref<Set<string>>(new Set());

const pathsWithChildren = computed<Set<string>>(() => {
  const set = new Set<string>();
  for (const f of props.fields) {
    if (!f.path) continue;
    const parts = f.path.split('.');
    let acc = '';
    for (let i = 0; i < parts.length - 1; i++) {
      acc = acc ? `${acc}.${parts[i]}` : parts[i];
      set.add(acc);
    }
  }
  return set;
});

const hasChildren = (field: ParsedField) => !!field.path && pathsWithChildren.value.has(field.path);

const isCoverageDetailsField = (field: ParsedField) => field.path === 'coverageLines' && field.type === 'array';

const coverageLineItems = computed<ParsedField[]>(() =>
  props.fields.filter(
    (f) => f.depth === 1 && f.type === 'object' && f.path?.startsWith('coverageLines.') && !f.path.slice('coverageLines.'.length).includes('.'),
  ),
);

const isCoverageLineItem = (field: ParsedField): boolean => {
  if (field.depth !== 1 || field.type !== 'object' || !field.path) return false;
  const prefix = 'coverageLines.';
  return field.path.startsWith(prefix) && !field.path.slice(prefix.length).includes('.');
};

// A header row for a line added this session (rendered from pending state, not merged output).
const isAddedLineHeader = (field: ParsedField): boolean =>
  !!field.path?.startsWith('__added.') && field.depth === 1;

// Any coverage-line header row: a merged-output line item or a line added this session.
const isAnyLineHeader = (field: ParsedField): boolean => isCoverageLineItem(field) || isAddedLineHeader(field);

// Display-time relabels (data is unchanged):
//   - the array field "coverageLines" is shown as "coverageDetails"
//   - each item ("_coverageLineItem") is shown by its coverageTypeName (e.g. "D&O")
const displayName = (field: ParsedField): string => {
  if (isCoverageDetailsField(field)) return 'coverageDetails';
  if (isAnyLineHeader(field) && field.coverageTypeName) return field.coverageTypeName;
  return field.name;
};

const showCoverageLinesModal = ref(false);
const openCoverageLinesModal = () => {
  showCoverageLinesModal.value = true;
};
const closeCoverageLinesModal = () => {
  showCoverageLinesModal.value = false;
};

// ---- Structural coverage-line operations (add / delete / recategorize) ----

// Rows synthesized for lines added this session live under a reserved path prefix so they
// can never collide with real merged-output paths.
const ADDED_PATH_PREFIX = '__added.';
const isAddedFieldPath = (path?: string): boolean => !!path?.startsWith(ADDED_PATH_PREFIX);
const addedKeyFromPath = (path: string): string => path.slice(ADDED_PATH_PREFIX.length).split('.')[0];

const lineIndexFromPath = (path: string): number | null => {
  const parts = path.split('.');
  if (parts[0] !== 'coverageLines' || parts.length < 2) return null;
  const idx = Number(parts[1]);
  return Number.isInteger(idx) && idx >= 0 ? idx : null;
};

// Stable line key for a coverage line: the server stamps lineKey onto the merged output
// once any line review exists; before that, merged and raw indices are identical, so the
// key is derived from the line's position.
const lineKeyFor = (field: ParsedField): string | null => {
  if (field.lineKey) return field.lineKey;
  if (!field.path) return null;
  if (isAddedFieldPath(field.path)) return addedKeyFromPath(field.path);
  const idx = lineIndexFromPath(field.path);
  return idx === null ? null : `raw-${idx}`;
};

// The coverage line item that owns a field: the item itself, or its parent line for
// nested fields.
const owningLineItem = (field: ParsedField): ParsedField | undefined => {
  if (!field.path?.startsWith('coverageLines.')) return undefined;
  if (isCoverageLineItem(field)) return field;
  const idx = lineIndexFromPath(field.path);
  if (idx === null) return undefined;
  return coverageLineItems.value.find((li) => li.path === `coverageLines.${idx}`);
};

const isLineFieldDeleted = (field: ParsedField): boolean => {
  const owner = isCoverageLineItem(field) ? field : owningLineItem(field);
  if (!owner) return false;
  const key = lineKeyFor(owner);
  return key ? isLineDeleted(key) : false;
};

const toggleLineDeleted = (field: ParsedField): void => {
  const key = lineKeyFor(field);
  if (!key) return;
  if (isLineDeleted(key)) {
    unmarkLineDeleted(key);
    return;
  }
  const idx = field.path && !isAddedFieldPath(field.path) ? lineIndexFromPath(field.path) : null;
  markLineDeleted(key, idx !== null ? `coverageLines.${idx}.` : undefined);
  if (recatSelectOpenFor.value === key) recatSelectOpenFor.value = null;
};

// Recategorize: a small per-line select, opened from the tag button.
const recatSelectOpenFor = ref<string | null>(null);
const toggleRecatSelect = (field: ParsedField): void => {
  const key = lineKeyFor(field);
  if (!key) return;
  recatSelectOpenFor.value = recatSelectOpenFor.value === key ? null : key;
};

const applyRecategorize = (field: ParsedField, event: Event): void => {
  const key = lineKeyFor(field);
  const selected = Number((event.target as HTMLSelectElement).value);
  const ct = (props.coverageTypes ?? []).find((c) => c.coverageTypeId === selected);
  if (!key || !ct) return;
  setLineRecategorize(key, ct.coverageTypeId, ct.coverageType);
  recatSelectOpenFor.value = null;
};

const undoRecategorize = (field: ParsedField): void => {
  const key = lineKeyFor(field);
  if (key) clearLineRecategorize(key);
};

// The staged new coverage type for a line, when it differs from what is displayed.
const pendingTypeChange = (field: ParsedField): string | undefined => {
  if (isAddedFieldPath(field.path)) return undefined;
  const key = lineKeyFor(field);
  const op = key ? getPendingLineOp(key) : undefined;
  if (!op || op.op === 'delete') return undefined;
  if (op.coverageTypeName && op.coverageTypeName !== field.coverageTypeName) return op.coverageTypeName;
  return undefined;
};

// Coverage types not used by any surviving line — the option list for add/recategorize.
// The import silently drops every line of a duplicated coverage type, so the pickers never
// offer a type already in use.
const usedCoverageTypeNames = computed<Set<string>>(() => {
  const used = new Set<string>();
  const note = (name?: string) => {
    const normalized = (name ?? '').trim().toLowerCase();
    if (normalized) used.add(normalized);
  };
  for (const li of coverageLineItems.value) {
    const key = lineKeyFor(li);
    const op = key ? getPendingLineOp(key) : undefined;
    if (op?.op === 'delete') continue;
    note(op?.coverageTypeName ?? li.coverageTypeName);
  }
  for (const op of pendingAddedLines.value) {
    if (op.lineKey.startsWith('new-')) note(op.coverageTypeName);
  }
  return used;
});

const availableCoverageTypes = computed<CoverageType[]>(() =>
  (props.coverageTypes ?? []).filter((ct) => !usedCoverageTypeNames.value.has(ct.coverageType.trim().toLowerCase()))
);

// Add coverage line: pick a type, then stage a pending line with one empty field per
// active coverageLines field-library entry (lineCoverageType is server-managed).
const showAddLineModal = ref(false);
const addLineTypeId = ref<number | null>(null);
const isAddingLine = ref(false);
const lineFieldLibrary = ref<PromptField[] | null>(null);

const openAddLineModal = (): void => {
  addLineTypeId.value = null;
  showAddLineModal.value = true;
};

const confirmAddLine = async (): Promise<void> => {
  const ct = (props.coverageTypes ?? []).find((c) => c.coverageTypeId === addLineTypeId.value);
  if (!ct) return;
  isAddingLine.value = true;
  try {
    if (!lineFieldLibrary.value) {
      lineFieldLibrary.value = await aiService.getAllPromptFields('coverageLines');
    }
    const fieldNames = lineFieldLibrary.value
      .filter((f) => f.isActive && f.fieldName !== 'lineCoverageType')
      .map((f) => f.fieldName);
    addPendingLine(ct.coverageTypeId, ct.coverageType, fieldNames);
    showAddLineModal.value = false;
  } catch (error) {
    console.error('Failed to load coverage line fields:', error);
  } finally {
    isAddingLine.value = false;
  }
};

// Rows synthesized for lines added this session (superseded previously-added lines render
// from the merged output like any other line).
const addedLineRows = computed<ParsedField[]>(() => {
  const rows: ParsedField[] = [];
  for (const op of pendingAddedLines.value) {
    if (!op.lineKey.startsWith('new-')) continue;
    rows.push({
      name: '_coverageLineItem',
      value: undefined,
      location: null,
      type: 'object',
      path: `${ADDED_PATH_PREFIX}${op.lineKey}`,
      depth: 1,
      coverageTypeName: op.coverageTypeName,
      coverageTypeId: op.coverageTypeId,
      lineKey: op.lineKey,
    });
    op.fields?.forEach((value, name) => {
      const meta = lineFieldLibrary.value?.find((m) => m.fieldName === name);
      const type =
        meta?.dataType === 'number' || meta?.dataType === 'decimal'
          ? 'number'
          : meta?.dataType === 'boolean'
            ? 'boolean'
            : 'string';
      rows.push({
        name,
        value,
        location: null,
        type,
        description: meta?.description,
        verificationLabel: meta?.description,
        path: `${ADDED_PATH_PREFIX}${op.lineKey}.${name}`,
        depth: 2,
        isEditable: true,
      });
    });
  }
  return rows;
});

// Seed values for a superseding add: the previously-added line's current merged values.
const seedFieldsFor = (owner: ParsedField): Array<{ name: string; value: string | number | boolean | undefined }> => {
  const prefix = `${owner.path}.`;
  return props.fields
    .filter((f) => f.path?.startsWith(prefix) && f.isEditable)
    .map((f) => ({ name: f.name, value: f.value as string | number | boolean | undefined }));
};

// The pending value for a field, looking through added-line ops as well as ordinary
// pending local changes.
const getFieldLocalValue = (field: ParsedField): string | number | boolean | undefined => {
  if (!field.path) return undefined;
  if (isAddedFieldPath(field.path)) {
    return getPendingLineField(addedKeyFromPath(field.path), field.name);
  }
  const owner = owningLineItem(field);
  const ownerKey = owner ? lineKeyFor(owner) : null;
  if (ownerKey?.startsWith('add-') && getPendingLineOp(ownerKey)) {
    return getPendingLineField(ownerKey, field.name);
  }
  return getLocalChange(field.path);
};

const isExpanded = (field: ParsedField) => !!field.path && !collapsedPaths.value.has(field.path);

const isFieldHidden = (field: ParsedField): boolean => {
  if (!field.path || collapsedPaths.value.size === 0) return false;
  for (const collapsed of collapsedPaths.value) {
    if (field.path.startsWith(collapsed + '.')) return true;
  }
  return false;
};

const toggleExpand = (field: ParsedField) => {
  if (!field.path) return;
  const next = new Set(collapsedPaths.value);
  if (next.has(field.path)) next.delete(field.path);
  else next.add(field.path);
  collapsedPaths.value = next;
};
const isFinishingReview = ref(false);
const showDiscardDialog = ref(false);
const showConfirmModal = ref(false);
const reviewPreview = ref<ReviewPreviewResponse | null>(null);
const isLoadingPreview = ref(false);
const reviewError = ref<string | null>(null);
// The reviewer's edit set and structural line operations, captured at finish time and
// persisted only when the review completes. There is no intermediate persistence.
const pendingReviewEdits = ref<ReviewEditInput[]>([]);
const pendingReviewLineOps = ref<ReviewLineOpInput[]>([]);

watch(promptResultIdRef, () => {
  reviewError.value = null;
  pendingReviewEdits.value = [];
  pendingReviewLineOps.value = [];
  recatSelectOpenFor.value = null;
});

// Finish review: build the edit set, preview the changes, and show confirmation if needed.
const finishReview = async () => {
  if (!props.promptResultId) {
    exitReviewMode();
    return;
  }

  isFinishingReview.value = true;
  reviewError.value = null;
  try {
    // Capture the reviewer's edits and line operations (nothing is persisted yet).
    pendingReviewEdits.value = buildEditPayload();
    pendingReviewLineOps.value = buildLineOpsPayload();

    // Fetch preview to check for policy-level changes the review would apply.
    isLoadingPreview.value = true;
    let preview: ReviewPreviewResponse | null = null;
    try {
      preview = await aiService.getReviewPreview(props.promptResultId, pendingReviewEdits.value, pendingReviewLineOps.value);
    } catch (previewError: any) {
      // handleResponse throws Error(message, { cause: res.response }), so the backend body is
      // on `.cause.data`. Preview runs the same server-side validation as the review itself, so
      // anything it rejects would reject the review too — surface it and stay in review mode
      // rather than proceeding into a call guaranteed to fail. We key off the stable `code` the
      // API now returns (VALIDATION_FAILED) instead of string-matching the message prose, which
      // silently broke on any backend rewording. A missing import target carries no such code
      // and is informational: the review can still complete without one.
      const response = previewError?.cause as { status?: number; data?: { code?: string; error?: string } } | undefined;
      const body = response?.data;
      const message = body?.error ?? previewError?.message ?? '';
      const isValidationFailure = body?.code === 'VALIDATION_FAILED';
      // The API reports a missing import target as a plain 500, so that is the only failure
      // allowed through. A network drop, gateway error or other 4xx would otherwise import
      // the changes without the reviewer ever seeing them.
      const mayBeMissingImportTarget = response?.status === 500;
      if (isValidationFailure || !mayBeMissingImportTarget) {
        reviewError.value = message || 'The changes could not be previewed. Please try again.';
        isLoadingPreview.value = false;
        return;
      }
      // A missing import target is informational and non-blocking: fall through and let
      // completeReview persist. Surfacing an error banner here would falsely flag a review
      // that then saves successfully (it stayed up after exitReviewMode).
    }
    isLoadingPreview.value = false;

    if (preview && hasReviewChanges(preview)) {
      // Show confirmation modal with diff
      reviewPreview.value = preview;
      showConfirmModal.value = true;
    } else {
      // No policy changes - proceed directly
      await completeReview();
    }
  } catch (error) {
    console.error('Failed to finish review:', error);
    isLoadingPreview.value = false;
  } finally {
    isFinishingReview.value = false;
  }
};

// Complete the review: persist the edits as a review, emit events, exit review mode, close modal.
const completeReview = async () => {
  if (!props.promptResultId) return;

  isFinishingReview.value = true;
  reviewError.value = null;
  try {
    await aiService.markAsReviewed(props.promptResultId, pendingReviewEdits.value, pendingReviewLineOps.value);
    emit('overrides-changed');
    emit('review-completed');
    pendingReviewEdits.value = [];
    pendingReviewLineOps.value = [];
    exitReviewMode();
    showConfirmModal.value = false;
    reviewPreview.value = null;
  } catch (error: any) {
    console.error('Failed to complete review:', error);
    reviewError.value = error?.message || 'The review could not be saved. Please try again.';
    showConfirmModal.value = false;
  } finally {
    isFinishingReview.value = false;
  }
};

// Cancel the confirmation modal and stay in review mode
const cancelConfirmModal = () => {
  showConfirmModal.value = false;
  reviewPreview.value = null;
};

const reviewRecords = computed(() => buildReviewRecords(reviewPreview.value));

// Cancel review: check for unsaved changes and show dialog if needed
const cancelReview = () => {
  reviewError.value = null;
  if (hasPendingChanges.value) {
    showDiscardDialog.value = true;
  } else {
    exitReviewMode();
  }
};

// Discard unsaved changes and exit review mode
const discardAndExit = () => {
  showDiscardDialog.value = false;
  exitReviewMode();
};

const filteredFields = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const searched = !query
    ? props.fields
    : props.fields.filter((field) => {
        const nameMatch = field.name.toLowerCase().includes(query);
        const labelMatch = field.verificationLabel?.toLowerCase().includes(query) || false;
        const pathMatch = field.path?.toLowerCase().includes(query) || false;
        const valueMatch = formatValue(field.value).toLowerCase().includes(query);
        const descMatch = field.description?.toLowerCase().includes(query) || false;
        return nameMatch || labelMatch || pathMatch || valueMatch || descMatch;
      });
  if (query) return searched;
  return searched.filter((f) => !isFieldHidden(f));
});

// The rendered list: filtered merged-output fields plus rows for lines added this session,
// inserted directly after the coverage-lines block (or appended when there is none).
const displayFields = computed<ParsedField[]>(() => {
  const base = filteredFields.value;
  const added = isReviewMode.value ? addedLineRows.value : [];
  if (added.length === 0) return base;
  let insertAt = -1;
  for (let i = 0; i < base.length; i++) {
    const path = base[i].path;
    if (path === 'coverageLines' || path?.startsWith('coverageLines.')) insertAt = i;
  }
  if (insertAt === -1) return [...base, ...added];
  return [...base.slice(0, insertAt + 1), ...added, ...base.slice(insertAt + 1)];
});

// Sorted overrides for history display
const sortedOverrides = computed(() => {
  return Array.from(overrides.value.values()).sort((a, b) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
});

const handleFieldClick = (field: ParsedField) => {
  if (isContainerField(field) && hasChildren(field)) {
    toggleExpand(field);
    return;
  }
  // Only emit if field has a location and not in review mode editing
  if (field.location) {
    emit('field-selected', field);
  }
};

const formatValue = (value: unknown, field?: ParsedField): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return JSON.stringify(value);
  if (isCurrencyField(field)) {
    const formatted = formatCurrency(value as number | string, true);
    if (formatted) return formatted;
  }
  return String(value);
};

const isSelected = (field: ParsedField) => {
  if (!props.selectedField || !field.location || !props.selectedField.location) return false;
  return props.selectedField?.path === field.path && props.selectedField?.location?.page === field.location?.page;
};

const clearSearch = () => {
  searchQuery.value = '';
};

// Check if field is a container (object/array without value)
const isContainerField = (field: ParsedField) => {
  return (field.type === 'object' || field.type === 'array') && field.value === undefined;
};

// Check if a field has an override or local change
const isFieldOverridden = (fieldPath: string): boolean => {
  // In review mode, check both saved overrides and pending local changes
  if (isReviewMode.value) {
    return hasOverride(fieldPath) || hasLocalChange(fieldPath);
  }
  // Otherwise, use the overriddenFields prop from the merged endpoint
  return props.overriddenFields?.includes(fieldPath) ?? false;
};

// Check if field has an unsaved local change (for amber styling)
const isFieldLocallyChanged = (fieldPath: string): boolean => {
  return hasLocalChange(fieldPath);
};

// Row-level pending indicator that also covers values routed into added-line ops.
const fieldHasPendingValue = (field: ParsedField): boolean => {
  if (!field.path) return false;
  return isFieldLocallyChanged(field.path) || getFieldLocalValue(field) !== undefined;
};

// Get the count of overrides (combines local state with prop)
const totalOverrideCount = computed(() => {
  if (isReviewMode.value) {
    return overrideCount.value;
  }
  return props.overriddenFields?.length ?? 0;
});

// Get the display value for a field (considering overrides).
// Only consult the composable's override map while reviewing — it is loaded at review start
// and reflects prior reviews. Outside review mode, props.fields already carry merged override
// values, so reading the (now stale) map would shadow freshly merged values after a completed
// review until a full page refresh.
const getDisplayValue = (field: ParsedField): unknown => {
  if (!field.path || !isReviewMode.value) return field.value;
  // A pending revert returns the field to its LLM original (field.value here is the merged
  // value with the soon-to-be-undone override applied).
  if (isReverted(field.path)) {
    return getOverride(field.path)?.originalValue ?? field.value;
  }
  const override = getOverride(field.path);
  return override ? override.overrideValue : field.value;
};

// Handle override value updates (stores locally, saves on Finish Review). Fields of added
// coverage lines route into the line's add operation — the backend rejects ordinary field
// edits on added lines, so their values always travel inside the add op.
const handleValueUpdate = (field: ParsedField, value: string | number | boolean) => {
  if (!field.path) return;
  if (isAddedFieldPath(field.path)) {
    setPendingLineField(addedKeyFromPath(field.path), field.name, value);
    return;
  }
  const owner = owningLineItem(field);
  const ownerKey = owner ? lineKeyFor(owner) : null;
  if (owner && ownerKey?.startsWith('add-')) {
    ensureSupersedingAdd(ownerKey, owner.coverageTypeId, owner.coverageTypeName, seedFieldsFor(owner));
    setPendingLineField(ownerKey, field.name, value);
    return;
  }
  setLocalOverride(field.path, value);
};

// Clear the pending value for a field routed into an added-line op; returns true when handled.
const clearAddedLinePending = (field: ParsedField): boolean => {
  if (!field.path) return false;
  if (isAddedFieldPath(field.path)) {
    setPendingLineField(addedKeyFromPath(field.path), field.name, undefined);
    return true;
  }
  const owner = owningLineItem(field);
  const ownerKey = owner ? lineKeyFor(owner) : null;
  if (ownerKey?.startsWith('add-') && getPendingLineOp(ownerKey)) {
    setPendingLineField(ownerKey, field.name, undefined);
    return true;
  }
  return false;
};

// Handle revert - restores a field to its LLM original value (before any override)
const handleRevert = (field: ParsedField) => {
  if (!field.path) return;
  if (clearAddedLinePending(field)) return;

  if (hasOverride(field.path)) {
    // A prior review's override exists: mark it for removal so submit emits a revert edit.
    // Works regardless of whether the original value was null.
    setLocalRevert(field.path);
  } else {
    // Only an unsaved this-session edit: drop it so the field returns to the AI value.
    revertLocalOverride(field.path);
  }
};

const handleClearPending = (field: ParsedField) => {
  if (!field.path) return;
  if (clearAddedLinePending(field)) return;
  revertLocalOverride(field.path);
};

// Handle clear error
const handleClearError = (field: ParsedField) => {
  if (!field.path) return;
  clearError(field.path);
};

// restrictedFields is keyed by AI field name, which matches ParsedField.name.
const getRestrictionReason = (field: ParsedField): string | undefined => {
  return props.policySourceRestriction?.restrictedFields?.[field.name];
};

const isFieldRestricted = (field: ParsedField): boolean => {
  return getRestrictionReason(field) !== undefined;
};

const handleExclusionToggle = (field: ParsedField, excluded: boolean) => {
  if (!field.path) return;
  setLocalExclusion(field.path, excluded);
};

const isFieldExcluded = (field: ParsedField): boolean => {
  if (!field.path) return false;
  return isExcluded(field.path);
};

// Format date for history
const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_SHORT);
};

// Format override value for display
const formatOverrideValue = (value: string | number | boolean | null): string => {
  if (value === null) return 'N/A';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden" data-testid="extracted-fields">
    <!-- Header -->
    <div class="flex shrink-0 flex-col gap-2 border-b border-slate-200 p-4">
      <div class="flex items-center gap-2">
        <List :class="['h-5 w-5', isMalformed ? 'text-amber-600' : 'text-blue-600']" />
        <h3 class="font-semibold text-slate-900">Extracted Fields</h3>
        <Badge
          v-if="isMalformed"
          variant="secondary"
          class="ml-auto border-amber-300 bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
          <AlertTriangle class="mr-1 inline-block h-3 w-3" />
          Malformed
        </Badge>
        <template v-else-if="fields.length > 0">
          <!-- Override count badge -->
          <Badge
            v-if="totalOverrideCount > 0"
            variant="secondary"
            class="border-green-300 bg-green-100 px-2 py-0.5 text-xs text-green-700">
            <Pencil class="mr-1 inline-block h-3 w-3" />
            {{ totalOverrideCount }} override{{ totalOverrideCount > 1 ? 's' : '' }}
          </Badge>
          <Badge
            variant="secondary"
            class="ml-auto px-2 py-0.5 text-xs">
            {{ filteredFields.length }}/{{ fields.length }}
          </Badge>
        </template>
      </div>

      <!-- Review Mode Button (only when promptResultId is available and fields exist) -->
      <div
        v-if="promptResultId && fields.length > 0 && !isMalformed"
        class="flex items-center gap-2">
        <!-- Review button (when not in review mode) -->
        <button
          v-if="!isReviewMode"
          @click="enterReviewMode"
          :disabled="isLoadingOverrides"
          class="btn gap-1 btn-sm btn-primary">
          <Loader2
            v-if="isLoadingOverrides"
            class="h-4 w-4 animate-spin" />
          <Pencil
            v-else
            class="h-4 w-4" />
          {{ reviewActionLabel }}
        </button>
        <!-- Finish Review and Cancel buttons (when in review mode) -->
        <template v-else>
          <button
            @click="cancelReview"
            :disabled="isFinishingReview || isSavingAll"
            class="btn gap-1 btn-ghost btn-sm">
            Cancel
          </button>
          <button
            @click="finishReview"
            :disabled="isFinishingReview || isSavingAll"
            class="btn gap-1 btn-sm btn-success">
            <Loader2
              v-if="isFinishingReview || isSavingAll"
              class="h-4 w-4 animate-spin" />
            <CheckCircle
              v-else
              class="h-4 w-4" />
            {{ reviewFinishLabel }}
            <Badge
              v-if="pendingChangeCount > 0"
              variant="secondary"
              class="ml-1 border-amber-300 bg-amber-100 px-1.5 py-0 text-[10px] text-amber-700">
              {{ pendingChangeCount }} unsaved
            </Badge>
          </button>
        </template>
      </div>
      <!-- Review error message -->
      <div
        v-if="reviewError"
        class="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
        <AlertTriangle class="h-4 w-4 shrink-0" />
        {{ reviewError }}
      </div>
    </div>

    <!-- Search Input -->
    <div
      v-if="fields.length > 0 && !isMalformed"
      class="shrink-0 border-b border-slate-200 px-4 py-2">
      <div class="relative">
        <Search class="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search fields..."
          @keydown.escape="clearSearch"
          class="w-full rounded-md border border-slate-200 bg-white py-1.5 pr-8 pl-8 text-sm placeholder-slate-400 transition-colors outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>

    <!-- Override History (collapsible, shown when there are overrides) -->
    <CollapsibleRoot
      v-if="isReviewMode && overrideCount > 0"
      v-model:open="isHistoryExpanded"
      class="shrink-0 border-b border-slate-200">
      <CollapsibleTrigger class="flex w-full items-center justify-between bg-slate-50 px-4 py-2 text-left transition-colors hover:bg-slate-100">
        <div class="flex items-center gap-2">
          <History class="h-4 w-4 text-slate-500" />
          <span class="text-sm font-medium text-slate-700">Override History</span>
          <Badge
            variant="secondary"
            class="px-1.5 py-0 text-[10px]">
            {{ overrideCount }}
          </Badge>
        </div>
        <ChevronDown
          v-if="!isHistoryExpanded"
          class="h-4 w-4 text-slate-400" />
        <ChevronUp
          v-else
          class="h-4 w-4 text-slate-400" />
      </CollapsibleTrigger>
      <CollapsibleContent class="max-h-[200px] overflow-y-auto bg-slate-50 px-4 pb-3">
        <div class="space-y-2">
          <div
            v-for="override in sortedOverrides"
            :key="override.id"
            class="rounded-md border border-slate-200 bg-white p-2 text-xs">
            <div class="mb-1 flex items-center justify-between">
              <span class="font-medium text-slate-900">{{ override.fieldPath }}</span>
              <span class="text-slate-400">{{ formatDate(override.updatedAt) }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-red-600 line-through">{{ formatOverrideValue(override.originalValue) }}</span>
              <span class="text-slate-400">&rarr;</span>
              <span class="font-medium text-green-600">{{ formatOverrideValue(override.overrideValue) }}</span>
            </div>
            <div class="mt-1 text-slate-400">by {{ override.userEmail }}</div>
          </div>
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>

    <!-- Malformed JSON State -->
    <div
      v-if="isMalformed"
      class="flex min-h-0 flex-1 flex-col overflow-hidden">
      <!-- Error Banner -->
      <div class="mx-4 mt-2 flex shrink-0 items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
        <AlertTriangle class="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p class="text-sm font-medium text-amber-800">Malformed Output</p>
          <p class="mt-0.5 text-xs text-amber-700">The AI output could not be parsed as structured fields. Raw output shown below for reference.</p>
        </div>
      </div>

      <!-- Raw Output View -->
      <div class="mt-3 flex min-h-0 flex-1 flex-col px-4 pb-4">
        <div class="mb-2 flex shrink-0 items-center gap-2">
          <span class="text-xs font-medium tracking-wide text-slate-500 uppercase">Raw Output</span>
          <Badge
            variant="secondary"
            class="bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
            Read Only
          </Badge>
        </div>
        <div class="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-300 bg-slate-50">
          <pre class="p-3 font-mono text-xs break-words whitespace-pre-wrap text-slate-700">{{ rawOutput }}</pre>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="fields.length === 0"
      class="flex flex-1 flex-col items-center justify-center p-4 text-slate-400">
      <p class="text-center text-sm">No extracted fields available</p>
    </div>

    <!-- No Results State -->
    <div
      v-else-if="filteredFields.length === 0"
      class="flex flex-1 flex-col items-center justify-center p-4 text-slate-400">
      <Search class="mb-2 h-8 w-8" />
      <p class="text-center text-sm">No fields match "{{ searchQuery }}"</p>
      <button
        @click="clearSearch"
        class="mt-2 text-sm text-blue-600 hover:underline">
        Clear search
      </button>
    </div>

    <!-- Fields List -->
    <div
      v-else
      class="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
      <div
        v-for="(field, index) in displayFields"
        :key="`${field.path || field.name}-${index}`"
        @click="handleFieldClick(field)"
        :style="{ marginLeft: `${(field.depth || 0) * 10}px` }"
        :class="[
          'relative rounded border p-2 transition-all',
          (field.location && !isReviewMode) || (isContainerField(field) && hasChildren(field)) ? 'cursor-pointer' : 'cursor-default',
          isFieldExcluded(field) ? 'opacity-60' : '',
          isReviewMode && isLineFieldDeleted(field) ? 'opacity-50' : '',
          isSelected(field)
            ? 'border-blue-500 bg-blue-50 shadow-sm'
            : isReviewMode && isLineFieldDeleted(field)
              ? 'border-red-300 bg-red-50'
              : isContainerField(field)
                ? 'border-slate-300 bg-slate-100'
                : fieldHasPendingValue(field)
                  ? 'border-amber-300 bg-amber-50'
                  : isFieldOverridden(field.path || '')
                    ? 'border-green-300 bg-green-50'
                    : field.location
                      ? 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
                      : 'border-slate-200 bg-slate-50 opacity-75',
        ]">
        <!-- Field Header -->
        <div class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <!-- Expand/collapse toggle for container fields with children -->
          <button
            v-if="isContainerField(field) && hasChildren(field)"
            type="button"
            @click.stop="toggleExpand(field)"
            :aria-expanded="isExpanded(field)"
            :aria-label="(isExpanded(field) ? 'Collapse ' : 'Expand ') + displayName(field)"
            class="rounded p-0.5 hover:bg-slate-200">
            <ChevronDown
              v-if="isExpanded(field)"
              class="h-3 w-3 shrink-0 text-slate-500" />
            <ChevronRight
              v-else
              class="h-3 w-3 shrink-0 text-slate-500" />
          </button>
          <!-- Spacer to keep name alignment for non-container child rows -->
          <span
            v-else-if="field.depth && field.depth > 0"
            class="inline-block h-3 w-3 shrink-0"
            aria-hidden="true" />

          <!-- Field name -->
          <span
            :class="[
              'text-xs font-medium break-all text-slate-900',
              isFieldExcluded(field) || (isReviewMode && isLineFieldDeleted(field)) ? 'line-through' : '',
            ]"
            >{{ displayName(field) }}</span
          >

          <!-- Staged coverage-type change (old name shown as the row label, new type here) -->
          <Badge
            v-if="isReviewMode && isAnyLineHeader(field) && pendingTypeChange(field)"
            variant="secondary"
            class="border-amber-300 bg-amber-100 px-1.5 py-0 text-[10px] text-amber-700">
            &rarr; {{ pendingTypeChange(field) }}
            <button
              type="button"
              class="ml-1 rounded hover:bg-amber-200"
              aria-label="Undo coverage type change"
              title="Undo coverage type change"
              @click.stop="undoRecategorize(field)">
              <X class="inline-block h-3 w-3" />
            </button>
          </Badge>
          <Badge
            v-if="isReviewMode && isAnyLineHeader(field) && isLineFieldDeleted(field)"
            variant="secondary"
            class="border-red-300 bg-red-100 px-1.5 py-0 text-[10px] text-red-700">
            deleted
          </Badge>
          <Badge
            v-if="isAddedLineHeader(field)"
            variant="secondary"
            class="border-green-300 bg-green-100 px-1.5 py-0 text-[10px] text-green-700">
            new line
          </Badge>

          <!-- Per-line structural controls: recategorize + delete/restore -->
          <span
            v-if="isReviewMode && isAnyLineHeader(field)"
            class="ml-auto flex items-center gap-1"
            @click.stop>
            <select
              v-if="recatSelectOpenFor === lineKeyFor(field)"
              class="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs"
              aria-label="Change coverage type"
              @change="applyRecategorize(field, $event)">
              <option
                value=""
                disabled
                selected>
                Change type…
              </option>
              <option
                v-for="ct in availableCoverageTypes"
                :key="ct.coverageTypeId"
                :value="ct.coverageTypeId">
                {{ ct.coverageType }}
              </option>
            </select>
            <button
              v-if="!isLineFieldDeleted(field) && (coverageTypes?.length ?? 0) > 0"
              type="button"
              class="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="Change coverage type"
              title="Change coverage type"
              @click.stop="toggleRecatSelect(field)">
              <Pencil class="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              :class="[
                'flex h-6 w-6 items-center justify-center rounded border transition-colors',
                isLineFieldDeleted(field)
                  ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                  : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
              ]"
              :aria-label="isLineFieldDeleted(field) ? 'Restore coverage line' : 'Delete coverage line'"
              :title="isLineFieldDeleted(field) ? 'Restore coverage line' : 'Delete coverage line'"
              @click.stop="toggleLineDeleted(field)">
              <Undo2
                v-if="isLineFieldDeleted(field)"
                class="h-3.5 w-3.5" />
              <Trash2
                v-else
                class="h-3.5 w-3.5" />
            </button>
          </span>

          <!-- Override indicator (amber for unsaved, green for saved) -->
          <span
            v-if="isFieldLocallyChanged(field.path || '')"
            class="h-1.5 w-1.5 rounded-full bg-amber-500"
            title="Unsaved change" />
          <span
            v-else-if="isFieldOverridden(field.path || '')"
            class="h-1.5 w-1.5 rounded-full bg-green-500"
            title="This field has been overridden" />

          <!-- PolicySource-restricted indicator -->
          <Tooltip
            v-if="isFieldRestricted(field)"
            :text="getRestrictionReason(field) ?? ''"
            position="top">
            <Badge
              variant="outline"
              class="border-amber-300 bg-amber-100 px-1.5 py-0 text-[10px] text-amber-800">
              <AlertTriangle class="mr-1 inline-block h-3 w-3" />
              Restricted
            </Badge>
          </Tooltip>

          <!-- Type badge -->
          <span
            v-if="field.type"
            class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-slate-600 uppercase">
            {{ field.type }}
          </span>

          <!-- Required indicator -->
          <span
            v-if="field.required"
            class="text-[10px] text-red-500">
            *
          </span>

          <!-- Page badge (only if field has location) -->
          <Badge
            v-if="field.location?.page"
            variant="outline"
            class="ml-auto px-1.5 py-0 text-[10px]">
            Page {{ field.location.page }}
          </Badge>
        </div>

        <!-- The reviewer-facing label. The descriptor's `description` is the model's
             extraction instruction and is deliberately never rendered here. -->
        <p
          v-if="field.verificationLabel"
          data-testid="field-verification-label"
          class="wrap-break-words mt-0.5 text-xs text-slate-500">
          {{ field.verificationLabel }}
        </p>

        <!-- Field Value / Editor -->
        <div
          v-if="!isContainerField(field)"
          :class="['mt-1', field.depth && field.depth > 0 ? 'pl-[1.125rem]' : '']">
          <!-- An entity the extraction resolved is corrected by picking, not typing. -->
          <div v-if="isReviewMode && field.path && entityPickerOptions(field.path).length > 0">
            <Combobox
              :model-value="entityPickerValue(field)"
              :options="entityPickerOptions(field.path)"
              :disabled="isFieldRestricted(field) || isFieldExcluded(field)"
              :data-testid="`entity-picker-${field.path}`"
              @update:model-value="(value: string) => handleValueUpdate(field, value)" />
            <p
              v-if="entityGroupFor(field)"
              class="mt-1 text-xs text-slate-500"
              :data-testid="`entity-group-${field.path}`">
              {{ entityGroupLabel(field.path) }}: {{ entityGroupFor(field) }}
            </p>
          </div>

          <!-- The reference lists are what makes a correction safe; without them a typed
               value would only fail at the create. -->
          <p
            v-else-if="isReviewMode && entityOptionsFailed && isEntityField(field.path)"
            class="mt-1 text-xs text-amber-700"
            :data-testid="`entity-options-error-${field.path}`">
            Reference data could not be loaded, so this field cannot be corrected. Reload the page
            to try again.
          </p>

          <!-- Editable Field (in review mode for editable types) -->
          <EditableField
            v-else-if="isReviewMode && field.isEditable && field.path"
            :field="field"
            :override="getOverride(field.path) || null"
            :is-pending="isPending(field.path)"
            :error="getError(field.path) || null"
            :has-local-change="fieldHasPendingValue(field)"
            :reverted="isReverted(field.path)"
            :local-change-value="getFieldLocalValue(field)"
            :disabled="isFieldRestricted(field) || isFieldExcluded(field) || isLineFieldDeleted(field)"
            :disabled-reason="getRestrictionReason(field)"
            :currency-format="isCurrencyField(field)"
            @update:value="(value) => handleValueUpdate(field, value)"
            @revert="handleRevert(field)"
            @clear-error="handleClearError(field)"
            @clear-pending="handleClearPending(field)" />

          <!-- Read-only display (non-review mode or non-editable types) -->
          <div
            v-else
            :class="['text-xs wrap-break-word text-slate-600', isFieldExcluded(field) ? 'line-through' : '']">
            {{ isEntityField(field.path) ? entityPickerValue(field) : formatValue(getDisplayValue(field), field) }}
          </div>

          <!-- Reference-data provenance: what the agentic lookup resolved this text to. -->
          <Badge
            v-if="field.path && matchBadge(field.path)"
            variant="secondary"
            :class="`mt-1 px-1.5 py-0 text-[10px] ${matchBadge(field.path)!.class}`"
            :title="matchBadge(field.path)!.title"
            :data-testid="`entity-match-${field.path}`">
            {{ matchBadge(field.path)!.label }}
          </Badge>

          <!-- Exclusion checkbox (hidden for restricted fields, which are already skipped,
               and for added/deleted lines, whose fields never import individually). -->
          <label
            v-if="isReviewMode && field.isEditable && field.path && !isFieldRestricted(field) && !isAddedFieldPath(field.path) && !isLineFieldDeleted(field)"
            class="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-slate-600"
            @click.stop>
            <input
              type="checkbox"
              class="checkbox checkbox-xs"
              :checked="isFieldExcluded(field)"
              @change="(e) => handleExclusionToggle(field, (e.target as HTMLInputElement).checked)" />
            <span>{{ exclusionLabel }}</span>
            <Badge
              v-if="hasLocalExclusionChange(field.path)"
              variant="secondary"
              class="border-amber-300 bg-amber-100 px-1 py-0 text-[10px] text-amber-700">
              unsaved
            </Badge>
          </label>
        </div>

        <!-- Coverage Lines edit-table launcher (vertically centered on the row) -->
        <button
          v-if="isCoverageDetailsField(field) && coverageLineItems.length > 0"
          type="button"
          @click.stop="openCoverageLinesModal"
          class="absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100"
          :aria-label="isReviewMode ? 'Edit coverage lines table' : 'View coverage lines table'"
          :title="isReviewMode ? 'Edit coverage lines table' : 'View coverage lines table'">
          <Pencil
            v-if="isReviewMode"
            class="h-4 w-4" />
          <Eye
            v-else
            class="h-4 w-4" />
        </button>

        <!-- Add coverage line (review mode; type picker offers only unused coverage types) -->
        <button
          v-if="isReviewMode && isCoverageDetailsField(field) && (coverageTypes?.length ?? 0) > 0"
          type="button"
          @click.stop="openAddLineModal"
          :class="[
            'absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100',
            coverageLineItems.length > 0 ? 'right-11' : 'right-2',
          ]"
          aria-label="Add coverage line"
          title="Add a coverage line the AI missed">
          <Plus class="h-4 w-4" />
        </button>
      </div>
    </div>

    <!-- Coverage Lines Edit Modal -->
    <CoverageLinesEditModal
      :show="showCoverageLinesModal"
      :coverage-lines-field="fields.find(isCoverageDetailsField) || null"
      :coverage-line-items="coverageLineItems"
      :all-fields="fields"
      :is-review-mode="isReviewMode"
      :get-override="getOverride"
      :is-pending="isPending"
      :get-error="getError"
      :has-local-change="hasLocalChange"
      :get-local-change="getLocalChange"
      :set-local-override="setLocalOverride"
      :revert-local-override="revertLocalOverride"
      :clear-error="clearError"
      :available-coverage-types="availableCoverageTypes"
      :pending-type-change="pendingTypeChange"
      :apply-recategorize="applyRecategorize"
      :undo-recategorize="undoRecategorize"
      :is-line-deleted="isLineFieldDeleted"
      @close="closeCoverageLinesModal" />

    <!-- Discard Changes Dialog -->
    <Teleport to="body">
      <div
        v-if="showDiscardDialog"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showDiscardDialog = false">
        <div class="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-xl">
          <div class="mb-4 flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle class="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 class="font-semibold text-slate-900">Discard unsaved changes?</h3>
              <p class="text-sm text-slate-500">You have {{ pendingChangeCount }} unsaved change{{ pendingChangeCount > 1 ? 's' : '' }}.</p>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <button
              @click="showDiscardDialog = false"
              class="btn btn-ghost btn-sm">
              Keep editing
            </button>
            <button
              @click="discardAndExit"
              class="btn btn-sm btn-error">
              Discard
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Confirm Policy Updates Modal -->
    <AppModal
      :show="showConfirmModal && !!reviewPreview"
      size="xxl"
      action-button-label="Confirm"
      :loading="isFinishingReview"
      :action-disabled="isFinishingReview"
      :handle-cancel="cancelConfirmModal"
      :handle-action="completeReview">
      <template #title>
        <div class="flex flex-col">
          <span class="text-xl">Confirm Policy Updates</span>
          <span class="text-xs font-normal break-normal text-slate-medium">
            Review changes to policy {{ reviewPreview?.policyNumber }} before confirming.
          </span>
        </div>
      </template>
      <template #content>
        <PolicyEditReviewPanel
          v-if="reviewPreview"
          :records="reviewRecords" />
      </template>
    </AppModal>

    <!-- Add Coverage Line Modal -->
    <AppModal
      :show="showAddLineModal"
      size="sm"
      action-button-label="Add line"
      :loading="isAddingLine"
      :action-disabled="isAddingLine || !addLineTypeId"
      :handle-cancel="() => (showAddLineModal = false)"
      :handle-action="confirmAddLine">
      <template #title>
        <div class="flex flex-col">
          <span class="text-xl">Add coverage line</span>
          <span class="text-xs font-normal break-normal text-slate-medium">
            Add a coverage line the AI missed, then fill in its values.
          </span>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col gap-2">
          <label
            for="add-line-coverage-type"
            class="text-sm font-medium text-slate-700">
            Coverage type
          </label>
          <select
            id="add-line-coverage-type"
            v-model.number="addLineTypeId"
            class="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm">
            <option
              :value="null"
              disabled>
              Select coverage type…
            </option>
            <option
              v-for="ct in availableCoverageTypes"
              :key="ct.coverageTypeId"
              :value="ct.coverageTypeId">
              {{ ct.coverageType }}
            </option>
          </select>
          <p
            v-if="availableCoverageTypes.length === 0"
            class="text-xs text-amber-700">
            No coverage types are available to add — every eligible type is already captured on a line.
          </p>
          <p
            v-else
            class="text-xs text-slate-500">
            Types come from the linked policy's lines (or the prompt's schema when no policy is linked), excluding any
            already captured on a line.
          </p>
        </div>
      </template>
    </AppModal>
  </div>
</template>
